import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ReportV5 from '../components/report_v5/ReportV5';
import ReportIcon from '../components/report_v5/ReportIcons';
import {
  fetchReportV5,
  regenerateReportV5,
  REPORT_STATUS,
} from '../api/reports';
import { toReportV5ViewModel } from '../adapters/reportV5Adapter';
import { markPending, markSeen } from '../utils/pendingReports';

/**
 * V5 报告页状态机。
 *
 * 状态：idle → loading → (generating 轮询，1-2s 间隔，60s 超时) → done / failed / error
 * - done           ：渲染 ReportV5（含 is_fallback=true 时顶部降级 banner）
 * - failed         ：HTTP 500 + status='failed'，可重试（POST 同 endpoint）
 * - error          ：4xx pre-flight（测评不存在 / 未完成）等不可恢复
 *
 * 路由参数 id：
 *  - 'preview' / 'demo' / 静态 /report/v5/preview → 走 mock
 *  - 其他 → 走真后端 GET /api/reports/{id}/
 *
 * 后端契约：docs/archive/v5/M5-integration-contract.md §API Contract（2026-05-20）
 */
const POLL_INTERVAL_MS = 1500; // 后端建议 1-2s
// V5 报告生成实际 1-3 分钟（主模型 retries=2 timeout=120s + fallback retries=1 timeout=120s，
// 最坏链路接近 5 分钟）。4 分钟超时既覆盖绝大多数正常生成，又不至于让真挂掉的请求
// 永远转圈。撞超时时仍走 ERROR 分支提示用户刷新重试。
const POLL_TIMEOUT_MS = 240000;

export default function ReportV5Page() {
  const params = useParams();
  const id = params.id || 'preview';
  const navigate = useNavigate();

  const [status, setStatus] = useState(REPORT_STATUS.IDLE);
  const [vm, setVm] = useState(null);
  const [error, setError] = useState(null);
  const [canRetry, setCanRetry] = useState(false);
  const pollStartedAt = useRef(null);
  const timerRef = useRef(null);

  const applyResult = useCallback(({ status: s, payload, error: err, canRetry: cr }) => {
    if (s === REPORT_STATUS.GENERATING) {
      pollStartedAt.current = pollStartedAt.current || Date.now();
      setStatus(REPORT_STATUS.GENERATING);
      return;
    }
    if (s === REPORT_STATUS.FAILED || s === REPORT_STATUS.ERROR) {
      pollStartedAt.current = null;
      setStatus(s);
      setError(err);
      setCanRetry(Boolean(cr));
      return;
    }
    // done
    pollStartedAt.current = null;
    setVm(toReportV5ViewModel(payload));
    setStatus(REPORT_STATUS.DONE);
  }, []);

  const load = useCallback(async () => {
    setStatus(REPORT_STATUS.LOADING);
    setError(null);
    setCanRetry(false);
    pollStartedAt.current = null;
    const res = await fetchReportV5(id);
    applyResult(res);
  }, [id, applyResult]);

  const retry = useCallback(async () => {
    setStatus(REPORT_STATUS.LOADING);
    setError(null);
    setCanRetry(false);
    pollStartedAt.current = Date.now();
    const res = await regenerateReportV5(id);
    applyResult(res);
  }, [id, applyResult]);

  useEffect(() => {
    load();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [load]);

  // 进入 GENERATING 时把 id 推入 pending.rough 桶 —— 顶导此时不亮气泡，
  // 由 PageLayout 全局后台 polling 检测到 done 后才挪到 ready 桶弹气泡。
  // 进 DONE 时用户已经在等待页看到了报告，调 markSeen 直接清掉，不该再弹。
  useEffect(() => {
    if (id === 'preview') return;
    if (status === REPORT_STATUS.GENERATING) markPending('rough', id);
    else if (status === REPORT_STATUS.DONE) markSeen('rough', id);
  }, [status, id]);

  // generating 轮询
  useEffect(() => {
    if (status !== REPORT_STATUS.GENERATING) return undefined;
    const tick = async () => {
      const elapsed = Date.now() - (pollStartedAt.current || Date.now());
      if (elapsed > POLL_TIMEOUT_MS) {
        setStatus(REPORT_STATUS.ERROR);
        setError('报告生成超时，请稍后刷新页面再试。');
        setCanRetry(true);
        return;
      }
      const res = await fetchReportV5(id);
      if (res.status === REPORT_STATUS.GENERATING) {
        timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        return;
      }
      applyResult(res);
    };
    timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [status, id, applyResult]);

  const handleUnlock = useCallback(() => {
    // 详细报告（付费）= 独立 6-Page 报告（产品决定 (a)，2026-05-20 与用户确认）。
    // 当前直接跳 /detailed 路由（preview 模式跳到 mock）；真实付费门将由后端
    // ?variant=detailed endpoint 在未付费时返回 403 拦下来。付费流程是独立 ticket。
    navigate(id === 'preview' ? '/report/v5/preview/detailed' : `/report/v5/${id}/detailed`);
  }, [id, navigate]);

  const handleRetestReady = useCallback((retestId) => {
    if (retestId) navigate(`/assessment/${retestId}`);
  }, [navigate]);

  // ---- 渲染 ----
  if (status === REPORT_STATUS.LOADING || status === REPORT_STATUS.IDLE) {
    return <StateView icon="spin" title="正在加载你的报告…" tip="解析答题数据中" />;
  }
  if (status === REPORT_STATUS.GENERATING) {
    return (
      <StateView
        icon="spin"
        title="报告正在生成中…"
        tip={'AI 正在根据你的作答撰写专属解读，通常需要 1-3 分钟，请稍候。你也可以先返回首页，生成完成后从顶部"历史记录"进入查看。'}
        secondaryLabel="返回首页"
        onSecondary={() => navigate('/')}
      />
    );
  }
  if (status === REPORT_STATUS.FAILED) {
    return (
      <StateView
        error
        icon="alert-triangle"
        title="报告生成失败"
        tip={error || '请重试一次。如多次失败，请联系管理员。'}
        primaryLabel={canRetry ? '重新生成' : null}
        onPrimary={canRetry ? retry : null}
        secondaryLabel="刷新页面"
        onSecondary={load}
      />
    );
  }
  if (status === REPORT_STATUS.ERROR) {
    return (
      <StateView
        error
        icon="shield"
        title="报告无法加载"
        tip={error || '请稍后刷新重试，或返回首页重新进入。'}
        primaryLabel="重试"
        onPrimary={load}
      />
    );
  }
  // done
  // preview 模式（id === 'preview'）不显示返回按钮：preview 通常是站点内 demo
  // 入口（无明确"上一页"），加按钮反而误导。真实测评报告才显示返回。
  const handleBack = id !== 'preview' ? () => navigate('/history') : undefined;
  return (
    <ReportV5
      vm={vm}
      assessmentId={id}
      onUnlock={handleUnlock}
      onBack={handleBack}
      onRetestReady={handleRetestReady}
    />
  );
}

function StateView({ icon, title, tip, error, primaryLabel, onPrimary, secondaryLabel, onSecondary }) {
  return (
    <div className="v5r-root">
      <div className="v5r-container">
        <div className={`v5r-state ${error ? 'error' : ''}`}>
          <div className="v5r-state-icon">
            {icon === 'spin' ? <span className="v5r-state-spin" /> : <ReportIcon name={icon} size={28} />}
          </div>
          <h2>{title}</h2>
          {tip && <p>{tip}</p>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 18 }}>
            {primaryLabel && onPrimary && (
              <button type="button" className="v5r-state-btn" onClick={onPrimary}>{primaryLabel}</button>
            )}
            {secondaryLabel && onSecondary && (
              <button
                type="button"
                className="v5r-state-btn"
                style={{ background: 'transparent', color: 'var(--v5-accent)', border: '1px solid var(--v5-line)' }}
                onClick={onSecondary}
              >
                {secondaryLabel}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
